import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Account } from "@/lib/types";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "только что";
  if (diff < 3600) return `${Math.floor(diff / 60)} мин`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} дн`;
  return d.toLocaleDateString();
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { day: "2-digit", month: "short" });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function initials(first?: string | null, last?: string | null, fallback = "?"): string {
  const f = (first ?? "").trim();
  const l = (last ?? "").trim();
  if (!f && !l) return fallback;
  return ((f[0] ?? "") + (l[0] ?? "")).toUpperCase() || fallback;
}

export function colorFromString(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 65% 45%)`;
}

export function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) + "…" : id;
}

export function mediaKindLabel(kind?: string | null): string {
  switch (kind) {
    case "album":
      return "🖼 Альбом";
    case "photo":
      return "📷 Фото";
    case "video":
      return "🎬 Видео";
    case "video_note":
      return "⭕ Кружок";
    case "voice":
      return "🎤 Голосовое";
    case "audio":
      return "🎵 Аудио";
    case "document":
      return "📎 Файл";
    default:
      return "📎 Вложение";
  }
}

/** Короткое текстовое превью сообщения для цитат-ответов. */
export function messagePreview(m: {
  text?: string | null;
  note?: string | null;
  media?: { kind?: string | null } | null;
  media_id?: string | null;
}): string {
  const t = m.text?.trim() || m.note;
  if (t) return t;
  if (m.media) return mediaKindLabel(m.media.kind);
  if (m.media_id) return "📎 Вложение";
  return "сообщение";
}

/** Строка последнего сообщения для списка чатов: «Вы: текст» / «📷 Фото».
 * pendingText — текст ещё не подтверждённого исходящего (см. chatOutgoingStatus) —
 * перекрывает last_message, чтобы список показывал то, что реально печатается/шлётся. */
export function chatPreview(
  chat: {
    last_message?: {
      direction: string;
      text: string | null;
      media_kind: string | null;
      service?: string | null;
    } | null;
    username?: string | null;
    tg_peer_id?: number;
  },
  pendingText?: string | null
): string {
  if (pendingText != null) return `Вы: ${pendingText.replace(/\s+/g, " ")}`;
  const lm = chat.last_message;
  if (!lm) return chat.username ? `@${chat.username}` : `id ${chat.tg_peer_id ?? ""}`.trim();
  const body = lm.text?.trim() || (lm.media_kind ? mediaKindLabel(lm.media_kind) : "сообщение");
  // Одну строку в списке: перевод строки внутри сообщения ломает вёрстку.
  const flat = body.replace(/\s+/g, " ");
  // Служебное («📞 Исходящий звонок») уже само говорит, чьё оно.
  return lm.direction === "out" && !lm.service ? `Вы: ${flat}` : flat;
}

/** Текст ещё не подтверждённого исходящего для превью в списке чатов. */
export function pendingPreviewText(item: { text: string | null; fileName: string | null }): string {
  const t = item.text?.trim();
  if (t) return t;
  return item.fileName ? `📎 ${item.fileName}` : "вложение";
}

export type OutgoingStatus = "sending" | "failed" | "sent" | "read" | null;

/** Статус последнего исходящего для иконки в списке чатов: часы/галочка/двойная галочка. */
export function chatOutgoingStatus(
  chat: {
    last_message?: { direction: string; tg_message_id: number; service?: string | null } | null;
    outbox_read_max_id?: number | null;
  },
  pending?: { status: "sending" | "failed" }[]
): OutgoingStatus {
  if (pending && pending.length > 0) {
    return pending.some((p) => p.status === "failed") ? "failed" : "sending";
  }
  const lm = chat.last_message;
  // Служебное («Вы очистили историю») — не наше сообщение собеседнику, галочек
  // доставки у него нет и в официальном клиенте.
  if (!lm || lm.direction !== "out" || lm.service) return null;
  if (chat.outbox_read_max_id != null && lm.tg_message_id <= chat.outbox_read_max_id) return "read";
  return "sent";
}

/** mm:ss (или h:mm:ss для длинного видео) — плеер голосовых/кружков/видео. */
export function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, "0")}` : `${mm}:${String(s).padStart(2, "0")}`;
}

export function bytesHuman(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Воркер поднимает сессию только для active/new/cooldown (см. workers/runner.py),
// и отправку режет assert_can_send. При остальных статусах чаты и сообщения в UI —
// это зеркало в БД, а не живой Telegram, поэтому такое состояние нужно показывать
// явно. Возвращает причину для оператора или null, если аккаунт в порядке.
const OFFLINE_LABELS: Record<string, string> = {
  disabled: "Аккаунт отключён — воркер его не поднимает.",
  banned: "Аккаунт забанен или удалён в Telegram.",
  frozen: "Аккаунт заморожен после PEER_FLOOD.",
  no_proxy: "Аккаунту не назначен рабочий прокси.",
};

export function accountOfflineReason(a: Account | null | undefined): string | null {
  if (!a) return null;
  const label = OFFLINE_LABELS[a.status];
  if (label) return a.last_error || label;
  if (a.cooldown_until && new Date(a.cooldown_until) > new Date())
    return a.last_error || `Аккаунт в cooldown до ${fmtDateTime(a.cooldown_until)}.`;
  return null;
}
